import axiosInstance from 'api/axiosInstance';
import { URL_SCHEDULER_READ_DATE_RANGE } from 'constants/urls';
import { parseOvernightPatrol } from 'utils/conflictDetection';

/**
 * Fetch schedules for a date range with optional filters.
 * This is the recommended endpoint for loading weekly/monthly schedule views.
 *
 * @param {Object} params - Query parameters
 * @param {Date} params.startDate - Start of date range (required)
 * @param {Date} params.endDate - End of date range (required)
 * @param {number} [params.contactId] - Filter by guard/employee ID (optional)
 * @param {number} [params.customerId] - Filter by customer/company ID (optional)
 * @param {number} [params.addressId] - Filter by location/address ID (optional)
 * @returns {Promise<{data: Array, totalResults: number}>} Schedule assignments with total count
 */
const getSchedulesByDateRange = async ({ startDate, endDate, contactId, customerId, addressId }) => {
  const payload = {
    StartDate: startDate.toISOString(),
    EndDate: endDate.toISOString(),
  };

  // Add optional filters only if provided
  if (contactId) {
    payload.ContactId = contactId;
  }
  if (customerId) {
    payload.CustomerId = customerId;
  }
  if (addressId) {
    payload.AddressId = addressId;
  }

  const { data } = await axiosInstance({
    method: 'GET',
    url: URL_SCHEDULER_READ_DATE_RANGE,
    params: payload,
    paramsSerializer: (params) => `payload=${JSON.stringify(params)}`,
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    data: null,
  });

  // API returns { TotalResults: number, Data: Array } or array directly
  const patrols = Array.isArray(data) ? data : data.Data || [];
  const totalResults = data.TotalResults || patrols.length;

  // Parse date strings to Date objects for frontend use
  // Also handle overnight patrols where end time appears before start time
  const parsedPatrols = patrols.map((patrol) => {
    const parsed = {
      ...patrol,
      StartTime: new Date(patrol.StartTime),
      EndTime: new Date(patrol.EndTime),
      WorkDate: new Date(patrol.WorkDate),
    };
    // Handle overnight patrols (end time before start time means it spans midnight)
    return parseOvernightPatrol(parsed);
  });

  return {
    data: parsedPatrols,
    totalResults,
  };
};

export default getSchedulesByDateRange;
